






































































































































import { TargetTrackerApi } from '@/api/target-tracker';
import { TargetTrackerAppointmentPayment } from '@/shared/target-tracker-appointment-payment';
import { AppointmentPaymentStatusType } from '@/shared/types/appointment-payment-status-type';
import { formatCurrentDate, formatDate } from '@/shared/utilities/date-utilities';
import { DateTime } from 'luxon';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const userModule = namespace('user');

const targetTrackerApi = new TargetTrackerApi();

@Component
export default class TargetTrackerEditPaymentDialog extends Vue {
    @userModule.Getter('isInternalKeyStakeholder') private isInternalKeyStakeholder!: boolean;
    @userModule.Getter('isHeadOfDepartment') private isHeadOfDepartment!: boolean;

    @Prop({ required: true }) private value!: boolean;
    @Prop({ required: true }) private payment!: TargetTrackerAppointmentPayment;
    @Prop({ required: true }) private paymentStatusType!: AppointmentPaymentStatusType;

    private isVisible: boolean = this.value;
    private isBusy: boolean = false;

    private instructedDate: string | Date | null = formatDate(this.payment.instructedDate, true);
    private signingDate: string | Date | null = formatDate(this.payment.signingDate, true);
    private bankedDate: string | Date | null = formatDate(this.payment.bankedDate, true);
    private clearedDate: string | Date | null = formatDate(this.payment.clearedDate, true);
    private debtorDate: string | Date | null = this.payment.debtorDate == null ? formatCurrentDate() : formatDate(this.payment.debtorDate, true);
    private noLongerProceedingDate: string | Date | null = this.payment.noLongerProceedingDate == null ? formatCurrentDate() : formatDate(this.payment.noLongerProceedingDate, true);
    private writtenOffDate: string | Date | null = this.payment.writtenOffDate == null ? formatCurrentDate() : formatDate(this.payment.writtenOffDate, true);

    private get title(): string {
        if (this.paymentStatusType.isSigningPending) {
            return 'Update as Signing Pending';
        } else if (this.paymentStatusType.isSigningBooked) {
            return 'Update as Signing Booked';
        } else if (this.paymentStatusType.isPartPayment) {
            return 'Update as Pending';
        } else if (this.paymentStatusType.isBanked) {
            return 'Update as Banked';
        } else if (this.paymentStatusType.isCleared) {
            return 'Update as Cleared';
        } else if (this.paymentStatusType.isDebtor) {
            return 'Update as Debtor';
        } else if (this.paymentStatusType.isNoLongerProceeding) {
            return 'Update as No Longer Proceeding';
        } else if (this.paymentStatusType.isWrittenOff) {
            return 'Update as Written Off';
        } else {
            return 'Update';
        }
    }

    private get showSigningDate(): boolean {
        return !this.paymentStatusType.isSigningPending;
    }

    private get showAmount(): boolean {
        return !this.paymentStatusType.isSigningPending && !this.paymentStatusType.isSigningBooked;
    }

    private get showBankedDate(): boolean {
        if (this.paymentStatusType.isSigningPending || this.paymentStatusType.isSigningBooked || this.paymentStatusType.isPartPayment) { return false; }

        return this.paymentStatusType.isDebtor || this.paymentStatusType.isNoLongerProceeding || this.paymentStatusType.isWrittenOff ? this.payment.bankedDate != null : true;
    }

    private get showClearedDate(): boolean {
        return this.paymentStatusType.isCleared;
    }

    private get showDebtorDate(): boolean {
        return this.paymentStatusType.isDebtor;
    }

    private get showNoLongerProceedingDate(): boolean {
        return this.paymentStatusType.isNoLongerProceeding;
    }

    private get showWrittenOffDate(): boolean {
        return this.paymentStatusType.isWrittenOff;
    }

    private get isSigningDateRequired(): boolean {
        return this.paymentStatusType.isSigningBooked;
    }

    private get canEditSigningDate(): boolean {
        return !this.paymentStatusType.isPartPayment;
    }

    private get canEditClearedDate(): boolean {
        return this.isInternalKeyStakeholder ? true : this.isHeadOfDepartment && this.paymentStatusType.isCleared;
    }

    private get canEditDebtorDate(): boolean {
        return this.isInternalKeyStakeholder ? true : this.paymentStatusType.isDebtor;
    }

    private get canEditNoLongerProceedingDate(): boolean {
        return this.isInternalKeyStakeholder ? true : this.paymentStatusType.isNoLongerProceeding;
    }

    private get canEditWrittenOffDate(): boolean {
        return this.isInternalKeyStakeholder ? true : this.isHeadOfDepartment && this.paymentStatusType.isWrittenOff;
    }

    private get bankedOrInstructedDate(): string | Date | null {
        return this.bankedDate == null ? this.instructedDate : this.bankedDate;
    }

    private get instructedDateText(): string {
        const instructedDate = this.instructedDate instanceof Date ? DateTime.fromJSDate(this.instructedDate, { zone: 'UTC' }) : DateTime.fromISO(this.instructedDate!, { zone: 'UTC' });
        return instructedDate.minus({ hours: instructedDate.hour, minutes: instructedDate.minute }).toFormat('dd/MM/yyyy');
    }

    private get bankedDateText(): string {
        const bankedDate = this.bankedDate instanceof Date ? DateTime.fromJSDate(this.bankedDate, { zone: 'UTC' }) : DateTime.fromISO(this.bankedDate!, { zone: 'UTC' });
        return bankedDate.minus({ hours: bankedDate.hour, minutes: bankedDate.minute }).toFormat('dd/MM/yyyy');
    }

    private get bankedOrInstructedDateText(): string {
        const date = this.bankedOrInstructedDate instanceof Date ? DateTime.fromJSDate(this.bankedOrInstructedDate, { zone: 'UTC' }) : DateTime.fromISO(this.bankedOrInstructedDate!, { zone: 'UTC' });
        return date.minus({ hours: date.hour, minutes: date.minute }).toFormat('dd/MM/yyyy');
    }

    @Watch('value')
    private onValueChanged(value: boolean): void {
        this.isVisible = value;
    }

    private async onSaveAsync(): Promise<void> {
        if (!await this.$validator.validateAll()) { return; }

        try {
            this.isBusy = true;

            await targetTrackerApi.updateTargetTrackerPaymentAsync(this.payment.id, {
                paymentStatusId: this.paymentStatusType.id!,
                partPayment: this.payment.partPayment!,
                index: this.payment.index!,
                value: this.paymentStatusType.isSigningPending || this.paymentStatusType.isSigningBooked ? null : this.payment.value,
                signingDate: this.paymentStatusType.isSigningPending ? null : this.signingDate,
                bankedDate: this.paymentStatusType.isSigningPending || this.paymentStatusType.isSigningBooked || this.paymentStatusType.isPartPayment ? null : this.bankedDate,
                clearedDate: this.paymentStatusType.isCleared ? this.clearedDate : null,
                debtorDate: this.paymentStatusType.isDebtor ? this.debtorDate : null,
                noLongerProceedingDate: this.paymentStatusType.isNoLongerProceeding ? this.noLongerProceedingDate : null,
                writtenOffDate: this.paymentStatusType.isWrittenOff ? this.writtenOffDate : null,
                version: this.payment.version,
            });

            this.$emit('ok');
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not update payment, please try again.`, type: 'is-danger' });
            }
        }

        this.isBusy = false;
    }

    private onCancel(): void {
        this.$emit('cancel');
    }
}
